import { Building03, Check, UserCircle, X } from '@timelyapp/icons/solid-icons';
import type { DropdownMenuItem } from '@timelyapp/react';
import {
  Box,
  Card,
  CustomIcon,
  DropdownMenu,
  Frame,
  Pill,
  Text,
  RadixTooltip as Tooltip,
} from '@timelyapp/react';
import React, { useMemo } from 'react';
import type { GetIntegrationsApiResponse } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import type { IntegrationConfiguration } from '../redux/tic.integrations.slice';

type Integration = GetIntegrationsApiResponse['data'][number];

/** Readable text for TIC `disconnection_reason` codes; unknown codes show as-is. */
const disconnectionReasonLabels: Record<string, string> = {
  plan_entitlement_lost: "your plan doesn't include Next-Gen Sync Integrations",
};

export const getDisconnectionTooltip = (reason: string | null | undefined) =>
  `This integration was disconnected, cause: ${
    (reason && disconnectionReasonLabels[reason]) || reason || 'Unknown'
  }`;

interface IntegrationCardContainerProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Card.Container>, 'children'> {
  integration: Integration;
}

const IntegrationCardContainer = React.memo(
  React.forwardRef<React.ElementRef<typeof Card.Container>, IntegrationCardContainerProps>(
    ({ integration, ...cardProps }, ref) => {
      const hasDisconnectedPersonalConnection = !!integration.personal?.connection?.disconnected_at;
      const hasActivePersonalConnection = !!integration.personal?.connection?.connected;
      const hasDisconnectedWorkspaceConnection = !!integration.account?.connection?.disconnected_at;
      const hasActiveWorkspaceConnection = !!integration.account?.connection?.connected;

      return (
        <Card.Container
          ref={ref}
          flexDirection="row"
          gap="medium"
          alignItems="flex-start"
          {...cardProps}
        >
          <Frame.Image size="xsmall" src={integration.icon_url!} />
          <Box.Column gap="small" flexGrow={1}>
            <Box.Row gap="medium" alignItems="center">
              <Text.Headline size="xsmall">{integration.title}</Text.Headline>
              {integration.experimental && (
                <Tooltip tooltip="This integration is experimental and may have limited functionality">
                  <span>
                    <CustomIcon.BorderlessStar width={16} height={16} />
                  </span>
                </Tooltip>
              )}
              <Box.Spacer />
              <Box.Row marginTop={-4} gap="medium">
                <Card.VisibleOnInteraction>
                  <Box.Row>
                    {!hasActivePersonalConnection && !hasDisconnectedPersonalConnection && (
                      <Pill variant="alert" size="small">
                        <X /> Identity unverified
                      </Pill>
                    )}
                    {!hasActiveWorkspaceConnection && !hasDisconnectedWorkspaceConnection && (
                      <Pill variant="alert" size="small">
                        <X /> Not connected
                      </Pill>
                    )}
                  </Box.Row>
                </Card.VisibleOnInteraction>
                {hasDisconnectedWorkspaceConnection && (
                  <Tooltip
                    tooltip={getDisconnectionTooltip(
                      integration.account?.connection?.disconnection_reason
                    )}
                  >
                    <Pill variant="danger" size="small">
                      <X /> Workspace disconnected
                    </Pill>
                  </Tooltip>
                )}
                {hasDisconnectedPersonalConnection && (
                  <Tooltip
                    tooltip={getDisconnectionTooltip(
                      integration.personal?.connection?.disconnection_reason
                    )}
                  >
                    <Pill variant="alert" size="small">
                      <X /> Identity disconnected
                    </Pill>
                  </Tooltip>
                )}
              </Box.Row>

              <Box.Row marginTop={-4} gap="medium">
                {hasActivePersonalConnection && (
                  <Pill variant="success" size="small">
                    <Check /> Identity verified
                  </Pill>
                )}
                {hasActiveWorkspaceConnection && (
                  <Pill variant="success" size="small">
                    <Check /> Connected
                  </Pill>
                )}
                {/* Show warning pill for incomplete setup - always show if setup_completed is false */}
                {hasActiveWorkspaceConnection &&
                  integration.account?.connection?.settings?.setup_completed === false && (
                    <Pill variant="alert" size="small">
                      Incomplete
                    </Pill>
                  )}
                {hasActivePersonalConnection &&
                  integration.personal?.connection?.settings?.setup_completed === false && (
                    <Pill variant="alert" size="small">
                      Incomplete
                    </Pill>
                  )}
              </Box.Row>
            </Box.Row>
            <Text size="xsmall" color="subtle" ellipsis={1}>
              {integration.description || 'No description available.'}
            </Text>
          </Box.Column>
        </Card.Container>
      );
    }
  )
);

IntegrationCardContainer.displayName = 'IntegrationCardContainer';

interface IntegrationCardInteractiveProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Card.Container>, 'children'> {
  integration: Integration;
  canManageWorkspace: boolean;
  onConfigurationChange: (config: IntegrationConfiguration) => void;
  /** Renders the card read-only (no setup/manage entry points), e.g. when the plan lacks TIC integrations */
  disabled?: boolean;
}

const IntegrationCardInteractive: React.FC<IntegrationCardInteractiveProps> = ({
  integration,
  canManageWorkspace,
  onConfigurationChange,
  disabled,
  ...cardProps
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Compute connection states once
  const hasActivePersonalConnection = !!integration.personal?.connection?.connected;
  const hasActiveWorkspaceConnection = !!integration.account?.connection?.connected;

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const items: DropdownMenuItem[] = [];

    // Account option - show if supported (not just if connected)
    if (integration.account?.supported) {
      const isSetupIncomplete =
        integration.account?.connection?.settings?.setup_completed === false;

      // Determine the modal to open based on connection and setup state
      let modal: 'setup_wizard' | 'manage';
      let defaultStep: string | undefined;
      let label: string;
      let sidenote: React.ReactNode;

      if (!hasActiveWorkspaceConnection) {
        // Not connected - show "Set up"
        modal = 'setup_wizard';
        label = 'Set up workspace integration';
      } else if (isSetupIncomplete) {
        // Connected but setup not completed - show "Continue setup"
        modal = 'setup_wizard';
        defaultStep = 'subscriptions';
        label = 'Continue workspace setup';
        sidenote = (
          <Pill variant="alert" size="small">
            Incomplete
          </Pill>
        );
      } else {
        // Connected and setup completed - show "Manage"
        modal = 'manage';
        label = 'Manage workspace integration';
        sidenote = (
          <Pill variant="success" size="small">
            Connected
          </Pill>
        );
      }

      items.push({
        type: 'item' as const,
        label,
        icon: <Building03 />,
        sidenote,
        disabled: !canManageWorkspace,
        onSelect: () =>
          onConfigurationChange({
            toolId: integration.id,
            scope: 'account',
            modal,
            defaultStep,
          }),
      });

      // Add separate "Manage" option if we're showing setup/continue
      if (hasActiveWorkspaceConnection && modal !== 'manage') {
        items.push({
          type: 'item' as const,
          label: 'Manage workspace integration',
          icon: <Building03 />,
          disabled: !canManageWorkspace,
          onSelect: () =>
            onConfigurationChange({
              toolId: integration.id,
              scope: 'account',
              modal: 'manage',
            }),
        });
      }
    }

    // Personal option - show if supported (not just if connected)
    if (integration.personal?.supported) {
      const isSetupIncomplete =
        integration.personal?.connection?.settings?.setup_completed === false;

      // Determine the modal to open based on connection and setup state
      let modal: 'setup_wizard' | 'manage';
      let defaultStep: string | undefined;
      let label: string;
      let sidenote: React.ReactNode;

      if (!hasActivePersonalConnection) {
        // Not connected - show "Set up"
        modal = 'setup_wizard';
        label = 'Set up personal integration';
      } else if (isSetupIncomplete) {
        // Connected but setup not completed - show "Continue setup"
        modal = 'setup_wizard';
        defaultStep = 'sync';
        label = 'Continue personal setup';
        sidenote = (
          <Pill variant="alert" size="small">
            Incomplete
          </Pill>
        );
      } else {
        // Connected and setup completed - show "Manage"
        modal = 'manage';
        label = 'Manage personal integration';
        sidenote = (
          <Pill variant="success" size="small">
            Connected
          </Pill>
        );
      }

      items.push({
        type: 'item' as const,
        label,
        icon: <UserCircle />,
        sidenote,
        onSelect: () =>
          onConfigurationChange({
            toolId: integration.id,
            scope: 'personal',
            modal,
            defaultStep,
          }),
      });

      // Add separate "Manage" option if we're showing setup/continue
      if (hasActivePersonalConnection && modal !== 'manage') {
        items.push({
          type: 'item' as const,
          label: 'Manage personal integration',
          icon: <UserCircle />,
          onSelect: () =>
            onConfigurationChange({
              toolId: integration.id,
              scope: 'personal',
              modal: 'manage',
            }),
        });
      }
    }

    return items;
  }, [
    integration.account?.supported,
    integration.account?.connection?.settings?.setup_completed,
    hasActiveWorkspaceConnection,
    integration.personal?.supported,
    integration.personal?.connection?.settings?.setup_completed,
    hasActivePersonalConnection,
    integration.id,
    canManageWorkspace,
    onConfigurationChange,
  ]);

  // Skip if provider doesn't support any scope
  if (!integration.personal?.supported && !integration.account?.supported) return null;

  if (disabled) {
    return <IntegrationCardContainer integration={integration} {...cardProps} />;
  }

  const hasAnyConnection = hasActivePersonalConnection || hasActiveWorkspaceConnection;

  // Admin users always get dropdown (showing all supported options)
  // Unless there are no connections at all, then make card clickable for account setup
  if (canManageWorkspace) {
    // If no connections exist and account is supported, make card clickable for account setup
    if (!hasAnyConnection && integration.account?.supported) {
      return (
        <IntegrationCardContainer
          integration={integration}
          interactive
          data-testid={`tic-integration-${integration.id}`}
          {...cardProps}
          onClick={() =>
            onConfigurationChange({
              toolId: integration.id,
              scope: 'account',
              modal: 'setup_wizard',
            })
          }
        />
      );
    }

    // Otherwise show dropdown with all options
    return (
      <DropdownMenu
        closeOnSelect
        onOpenChange={setIsOpen}
        open={isOpen}
        sideOffset={-(4 * 4)}
        trigger={
          <IntegrationCardContainer
            interactive
            data-testid={`tic-integration-${integration.id}`}
            {...cardProps}
            selected={isOpen || cardProps.selected}
            integration={integration}
          />
        }
        align="center"
        side="bottom"
        width={370}
        items={dropdownItems}
      />
    );
  }

  // Non-admin with personal connection: Show dropdown with personal options only
  if (!canManageWorkspace && hasActivePersonalConnection) {
    const personalItems = dropdownItems.filter(
      item => item.type === 'item' && item.icon?.type === UserCircle
    );

    if (personalItems.length > 0) {
      return (
        <DropdownMenu
          closeOnSelect
          onOpenChange={setIsOpen}
          open={isOpen}
          sideOffset={-(4 * 4)}
          trigger={
            <IntegrationCardContainer
              interactive
              data-testid={`tic-integration-${integration.id}`}
              {...cardProps}
              selected={isOpen || cardProps.selected}
              integration={integration}
            />
          }
          align="center"
          side="bottom"
          width={370}
          items={personalItems}
        />
      );
    }
  }

  // Non-admin without personal connection: clickable card opens personal setup
  if (!canManageWorkspace && !hasActivePersonalConnection && integration.personal?.supported) {
    return (
      <IntegrationCardContainer
        integration={integration}
        interactive
        {...cardProps}
        onClick={() =>
          onConfigurationChange({
            toolId: integration.id,
            scope: 'personal',
            modal: 'setup_wizard',
          })
        }
      />
    );
  }

  // Fallback - should not reach here but just in case
  return <IntegrationCardContainer integration={integration} {...cardProps} />;
};

// Export as IntegrationCard for cleaner imports
export const IntegrationCard = IntegrationCardInteractive;
